import { Download as DownloadIcon, FilterAlt as FilterAltIcon } from '@mui/icons-material';
import { Button, Chip, FormControl, InputLabel, MenuItem, Select, Switch, Tab, Tabs, Typography } from '@mui/material';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { compact, startCase } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { getActiveShoragesCountForAllSections } from './ComponentsCheckHelpers';
import EmptyMessage from '../../components/EmptyMessage';
import { CookingStationName, FCStatus, FoodComponent, FoodComponentStationTitle, Kitchen } from '../../libs';
import ComponentWeightBufferEditor from './ComponentWeightBufferEditor/ComponentWeightBufferEditor';
import CustomAutocomplete from './CustomAutocomplete';
import SectionTable from './Desktop/SectionTable';
import DownloadPopup from './DownloadPopup';
import Export from './Export';
import FilterItems from './FilterItems';
import { COMPONENT_CHECK_STATIONS_STORAGE_KEY, getDefaultFilters, getPersistedStationsFilter } from './helpers';
import HoursSelector from './HoursSelector';
import { ComponentsCheckFilters, ComponentStatus, ViewTypes } from './types';
import { AppContext } from '../../App';
import { useKitchenComponents } from '../../hooks';
import MobileSectionTable from './Mobile/SectionTable/MobileSectionTable';
import { useDocumentMedia } from '../../libs/utils/document';
import { getAvailableHoursChanges, getComponentsWithNonZeroCount } from '../../libs/utils/foodComponentHelper';
import getPercComponentsCompleted from '../../libs/utils/getPercComponentsCompleted';
import FiltersDrawer from '../LeftoverMetrics/Common/FiltersDrawer';

interface ComponentsCheckProps {
  kitchen: string;
  date: string;
}

export { ViewTypes };

const ComponentsCheck = ({ kitchen, date }: ComponentsCheckProps) => {
  const {
    foodComponents: rawFoodComponents,
    componentsLoading,
    hasNext: componentsHasNext
  } = useKitchenComponents(kitchen, date);
  const theme = useTheme();
  const appContext = useContext(AppContext);
  const [isFilterOpened, setIsFilterOpened] = useState(false);
  const [view, setView] = useState<ViewTypes>(ViewTypes.SECTIONS);
  const { isMobile, isTablet } = useDocumentMedia();
  const [selectedComponentStation, setSelectedComponentStation] = useState<string>(Object.keys(FoodComponentStationTitle)[0]);
  const [highlightedComponentId, setHighlightedComponentId] = useState<string | null>(null);
  const [showAllUnits, setShowAllUnits] = useState(false);
  const [selectedHours, setSelectedHours] = useState<string>('');
  const [selectedComponentsForPrint, setSelectedComponentsForPrint] = useState<FoodComponent[]>([]);
  const [componentCheckFilters, setComponentCheckFilters] = useState<ComponentsCheckFilters>(() => {
    const defaultFilters = getDefaultFilters();
    const persistedStations = getPersistedStationsFilter();

    return persistedStations.length ? { ...defaultFilters, stations: persistedStations } : defaultFilters;
  });

  const exportComponentStatusModalRef = useRef<any>(null);
  const componentWeightBufferEditorModalRef = useRef<any>(null);
  const desktopContainerRef = useRef<HTMLDivElement>(null);

  const setSelectedComponents = useCallback((component: FoodComponent, isSelected: boolean) => {
    setSelectedComponentsForPrint((prev) => {
      if (isSelected) {
        return prev.some((c) => c.id === component.id) ? prev : [...prev, component];
      }
      return prev.filter((c) => c.id !== component.id);
    });
  }, []);
  const clearHighlight = useCallback(() => setHighlightedComponentId(null), []);

  useEffect(() => {
    setSelectedComponentsForPrint([]);
  }, [selectedComponentStation]);

  useEffect(() => {
    if (
      view === ViewTypes.SECTIONS &&
      componentCheckFilters.stations.length > 0 &&
      !componentCheckFilters.stations.includes(selectedComponentStation as CookingStationName)
    ) {
      setSelectedComponentStation(componentCheckFilters.stations[0]);
    }
  }, [componentCheckFilters.stations, selectedComponentStation, view]);

  useEffect(() => {
    if (componentCheckFilters.stations.length === 0) return;

    localStorage.setItem(COMPONENT_CHECK_STATIONS_STORAGE_KEY, JSON.stringify(componentCheckFilters.stations));
  }, [componentCheckFilters.stations]);

  // Memoised so the lists derived below keep their identity between renders; the table rows are React.memo'd.
  const foodComponents = useMemo(() => getComponentsWithNonZeroCount(rawFoodComponents), [rawFoodComponents]);
  const shortages = useMemo(() => getActiveShoragesCountForAllSections(foodComponents), [foodComponents]);

  const filteredFoodComponents = useMemo(() => {
    return foodComponents.filter(
      (fc) =>
        (!componentCheckFilters.stations.length ||
          fc.cookingStations?.some((s) => componentCheckFilters.stations.includes(s.name))) &&
        (!componentCheckFilters.statuses.length ||
          (componentCheckFilters.statuses.includes(ComponentStatus.verified) && fc.isVerified) ||
          (fc.cookingStations?.some((cs) => cs?.status === FCStatus.completed) &&
            componentCheckFilters.statuses.includes(ComponentStatus.completed)) ||
          (componentCheckFilters.statuses.includes(ComponentStatus.pending) &&
            fc.cookingStations?.some((cs) => cs?.status === FCStatus.pending)))
    );
  }, [foodComponents, componentCheckFilters]);

  const autocompleteOptions = useMemo(
    () =>
      (ViewTypes.OVERVIEW === view ? filteredFoodComponents : foodComponents).flatMap((fc) =>
        compact(
          (fc.cookingStations ?? []).map((station) =>
            station.name === CookingStationName.portioning
              ? null
              : {
                  label: `${fc.name.en} - ${FoodComponentStationTitle[station.name as keyof typeof FoodComponentStationTitle]}`,
                  value: fc.id,
                  station: station.name,
                  componentName: fc.name.en
                }
          )
        )
      ),
    [filteredFoodComponents, foodComponents, view]
  );

  const availableHours = useMemo(() => getAvailableHoursChanges(rawFoodComponents), [rawFoodComponents]);

  const overviewComponents = useMemo(
    () => filteredFoodComponents.filter((fc) => !highlightedComponentId || fc.id === highlightedComponentId),
    [filteredFoodComponents, highlightedComponentId]
  );
  const stationComponents = useMemo(
    () =>
      filteredFoodComponents.filter((fc) =>
        fc.cookingStations?.some((s) => s.name === (selectedComponentStation as CookingStationName))
      ),
    [filteredFoodComponents, selectedComponentStation]
  );
  const mobileStationComponents = useMemo(
    () =>
      foodComponents.filter((fc) => fc.cookingStations?.some((s) => s.name === (selectedComponentStation as CookingStationName))),
    [foodComponents, selectedComponentStation]
  );

  useEffect(() => {
    if (!isMobile && foodComponents.length > 0 && desktopContainerRef.current) {
      desktopContainerRef.current.scrollTop = 0;
    }
  }, [isMobile, foodComponents.length]);

  const selectedIdsSet = useMemo(() => new Set(selectedComponentsForPrint.map((c) => c.id)), [selectedComponentsForPrint]);

  const scrollToTopAfterHorizontalScroll = () => {
    setTimeout(() => {
      if (desktopContainerRef.current) {
        desktopContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 300);
  };
  useEffect(() => {
    if (availableHours.length > 0 && !selectedHours) {
      const lastHour = availableHours[availableHours.length - 1];
      setSelectedHours(lastHour);
    }
  }, [availableHours, selectedHours]);

  const topButtonStyle = (key: string) => ({
    textTransform: 'uppercase',
    fontSize: '14px',
    fontWeight: 500,
    minHeight: '42px',
    backgroundColor: theme.palette.white,
    p: '8px',
    color: selectedComponentStation === key ? theme.palette.primary.main : theme.palette.neutral500,
    '&:hover': {
      backgroundColor: 'transparent'
    }
  });

  if (isMobile) {
    return (
      <Box sx={{ width: '100%', my: 2, display: 'flex', flexDirection: 'column', maxHeight: 'auto' }}>
        <Box
          sx={{
            width: '100%'
          }}
        >
          <CustomAutocomplete
            options={autocompleteOptions}
            onChange={(value) => {
              if (value) {
                if (value.station) {
                  if (ViewTypes.SECTIONS === view) {
                    setComponentCheckFilters({
                      ...componentCheckFilters,
                      stations: [
                        ...componentCheckFilters.stations.filter((s) => s === value.station),
                        value.station as CookingStationName
                      ]
                    });
                    setSelectedComponentStation(value.station);
                  }
                  setHighlightedComponentId(value.value);
                }
              } else {
                setHighlightedComponentId(null);
              }
            }}
            placeholder="Search for component"
            width="100%"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, width: '100%', px: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="contained"
              sx={{
                color: '#515050',
                backgroundColor: '#E6F3EC',
                fontSize: '14px',
                fontWeight: 600,
                padding: '8px 16px',
                borderRadius: '8px',
                textTransform: 'none',
                height: '40px',
                '&:hover': {
                  backgroundColor: '#E6F3EC',
                  color: '#515050'
                }
              }}
              onClick={() => componentWeightBufferEditorModalRef.current?.open()}
            >
              Buffers
            </Button>
            <Export
              components={selectedComponentsForPrint}
              date={date}
              kitchen={kitchen as Kitchen}
              buffers={appContext.kdsKitchen?.componentWeightBuffers ?? []}
              isDownloadDisabled={false}
              selectedStation={selectedComponentStation}
            />
            <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>All Units</Typography>
            <Switch checked={showAllUnits} onChange={(e) => setShowAllUnits(e.target.checked)} />
          </Box>
        </Box>
        <FormControl sx={{ width: '100%', my: 2, px: 1 }}>
          <Box>
            <Select
              sx={{
                width: '60%',
                borderRadius: '8px'
              }}
              MenuProps={{
                slotProps: {
                  paper: {
                    style: {
                      marginLeft: '-8px',
                      maxHeight: '300px'
                    }
                  }
                }
              }}
              value={selectedComponentStation}
              onChange={(data: any) => setSelectedComponentStation(data.target.value)}
            >
              {Object.entries(FoodComponentStationTitle).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  <span>{value}</span>
                  <Chip
                    sx={{
                      background: theme.palette.primary300,
                      paddingLeft: '2px',
                      paddingRight: '2px',
                      paddingY: '3px',
                      height: 'min-content',
                      display: 'inline-block',
                      marginLeft: '5px'
                    }}
                    label={`${getPercComponentsCompleted(key, foodComponents)}%`}
                  />
                </MenuItem>
              ))}
            </Select>

            {shortages[selectedComponentStation] && shortages[selectedComponentStation].total > 0 && (
              <Stack
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: theme.palette.secondaryRed50,
                  mt: '12px',
                  borderRadius: '10px',
                  p: '10px'
                }}
              >
                <Typography>Shortage Reported</Typography>
                <Stack sx={{ backgroundColor: 'red', ml: '6px', borderRadius: '15px' }}>
                  <Typography sx={{ color: 'white', mx: '6px' }}>{shortages[selectedComponentStation].total}</Typography>
                </Stack>
              </Stack>
            )}
            <Button
              variant="outlined"
              sx={{
                ml: '15px',
                fontSize: '16px',
                padding: '10px',
                color: theme.palette.neutral900,
                borderColor: theme.palette.neutral700,
                borderRadius: '10px',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent',
                  borderColor: theme.palette.neutral700
                },
                width: '35%'
              }}
              onClick={() => exportComponentStatusModalRef.current?.open()}
            >
              Download
            </Button>
            <DownloadPopup
              exportComponentStatusModalRef={exportComponentStatusModalRef}
              theme={theme}
              kitchen={kitchen}
              mt="-5px"
              spacing={2}
              width="100%"
              stackDirection="column"
            />
            <ComponentWeightBufferEditor
              componentWeightBufferEditorModalRef={componentWeightBufferEditorModalRef}
              theme={theme}
            />
          </Box>
        </FormControl>

        <HoursSelector
          selectedHours={selectedHours}
          onHoursChange={setSelectedHours}
          isMobile={true}
          availableHours={availableHours}
        />

        <Stack direction="row" sx={{ px: 1 }}>
          {foodComponents.length > 0 ? (
            <MobileSectionTable
              showAllUnits={showAllUnits}
              key={selectedComponentStation}
              selectedComponentStation={selectedComponentStation as CookingStationName}
              setSelectedComponentStation={setSelectedComponentStation}
              foodComponents={mobileStationComponents}
              highlightedComponentId={highlightedComponentId}
              onHighlightEnd={clearHighlight}
              selectedHours={selectedHours}
            />
          ) : (
            !componentsLoading && <EmptyMessage style={{ textAlign: 'center', width: '100%' }} label="No Data" />
          )}
        </Stack>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={componentsLoading && !appContext.isOffline && !componentsHasNext}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    );
  } else {
    return (
      <Box
        ref={desktopContainerRef}
        sx={{
          height: '90vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          scrollbarWidth: 'none',
          mx: '30px',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            py: '20px',
            columnGap: '10px',
            rowGap: '20px',
            width: '100%'
          }}
        >
          <Box
            sx={{
              overflow: 'visible',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              columnGap: '20px'
            }}
          >
            <Button
              startIcon={<FilterAltIcon />}
              variant="outlined"
              onClick={() => setIsFilterOpened(true)}
              sx={{
                color: theme.palette.black,
                backgroundColor: theme.palette.white,
                borderColor: theme.palette.black,
                borderRadius: '5px',
                paddingX: '22px',
                paddingY: '8px',
                height: '40px',
                borderWidth: '1.5px',
                '&:hover': {
                  backgroundColor: theme.palette.grey[200],
                  borderColor: theme.palette.black,
                  borderWidth: '1.5px'
                }
              }}
            >
              <Typography
                sx={{
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                FILTER
              </Typography>
            </Button>
            <HoursSelector
              selectedHours={selectedHours}
              onHoursChange={setSelectedHours}
              isMobile={false}
              availableHours={availableHours}
            />
            <FormControl size="small" sx={{ minWidth: 145 }}>
              <InputLabel
                id="hours-selector-label"
                sx={{
                  fontWeight: 400,
                  color: theme.palette.neutral500
                }}
              >
                View:
              </InputLabel>
              <Select
                label="View:"
                value={view}
                onChange={(e) => {
                  setHighlightedComponentId(null);
                  if (e.target.value === ViewTypes.OVERVIEW) {
                    setSelectedComponentStation(ViewTypes.OVERVIEW);
                    setComponentCheckFilters({ ...componentCheckFilters, stations: [] });
                  } else {
                    setSelectedComponentStation(CookingStationName.commissary);
                    setComponentCheckFilters({ ...componentCheckFilters, stations: [CookingStationName.commissary] });
                  }
                  setView(e.target.value);
                }}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  height: '40px',
                  '& .MuiSelect-select': {
                    py: 1
                  }
                }}
              >
                {Object.values(ViewTypes).map((view) => (
                  <MenuItem key={view} value={view}>
                    {startCase(view)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box
            sx={{
              overflow: 'visible',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              columnGap: '10px',
              flexWrap: 'wrap',
              rowGap: '20px'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="contained"
                  sx={{
                    color: '#515050',
                    backgroundColor: '#E6F3EC',
                    fontSize: '14px',
                    fontWeight: 600,
                    padding: '8px 16px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    height: '40px',
                    '&:hover': {
                      backgroundColor: '#E6F3EC',
                      color: '#515050'
                    }
                  }}
                  onClick={() => componentWeightBufferEditorModalRef.current?.open()}
                >
                  Buffers
                </Button>
                <Export
                  components={selectedComponentsForPrint}
                  date={date}
                  kitchen={kitchen as Kitchen}
                  buffers={appContext.kdsKitchen?.componentWeightBuffers ?? []}
                  isDownloadDisabled={false}
                  selectedStation={selectedComponentStation}
                />
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>All Units</Typography>
                <Switch checked={showAllUnits} onChange={(e) => setShowAllUnits(e.target.checked)} />
              </Box>
            </Box>
            <CustomAutocomplete
              options={autocompleteOptions}
              onChange={(value) => {
                if (value) {
                  if (value.station) {
                    setHighlightedComponentId(value.value);
                    if (ViewTypes.SECTIONS === view) {
                      setComponentCheckFilters({
                        ...componentCheckFilters,
                        stations: [
                          ...componentCheckFilters.stations.filter((s) => s !== value.station),
                          value.station as CookingStationName
                        ]
                      });
                      setSelectedComponentStation(value.station);
                      scrollToTopAfterHorizontalScroll();
                    }
                  }
                } else {
                  setHighlightedComponentId(null);
                }
              }}
              placeholder="Search for component"
              width={350}
            />
            <Button
              startIcon={<DownloadIcon />}
              variant="outlined"
              sx={{
                color: theme.palette.black,
                backgroundColor: theme.palette.white,
                borderColor: theme.palette.black,
                borderRadius: '5px',
                paddingX: '22px',
                paddingY: '8px',
                height: '40px',
                borderWidth: '1.5px',
                '&:hover': {
                  backgroundColor: theme.palette.grey[200],
                  borderColor: theme.palette.black,
                  borderWidth: '1.5px'
                }
              }}
              onClick={() => exportComponentStatusModalRef.current?.open()}
            >
              <Typography
                sx={{
                  fontWeight: '500',
                  fontSize: '14px'
                }}
              >
                Download
              </Typography>
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: '15px' }}>
          <Tabs
            variant="scrollable"
            scrollButtons={false}
            value={selectedComponentStation}
            onChange={(_: React.SyntheticEvent, newValue: string) => {
              setSelectedComponentStation(newValue);
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            {view === ViewTypes.OVERVIEW && (
              <Tab value={ViewTypes.OVERVIEW} label={'Overview'} sx={topButtonStyle(ViewTypes.OVERVIEW)} />
            )}
            {view === ViewTypes.SECTIONS &&
              componentCheckFilters.stations.map(
                (key, index) =>
                  key !== 'portioning' && (
                    <Tab
                      key={index}
                      disabled={foodComponents.length === 0}
                      value={key}
                      label={FoodComponentStationTitle[key as keyof typeof FoodComponentStationTitle]}
                      icon={
                        <Box
                          sx={{
                            background: theme.palette.primary100,
                            width: '45px',
                            height: '24px',
                            borderRadius: '100px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          <Typography sx={{ color: theme.palette.black, fontSize: '13px', fontWeight: 400 }} align="center">
                            {`${getPercComponentsCompleted(key, foodComponents)}%`}
                          </Typography>
                        </Box>
                      }
                      iconPosition="end"
                      sx={topButtonStyle(key)}
                    />
                  )
              )}
          </Tabs>
        </Box>
        {foodComponents.length > 0 ? (
          view === ViewTypes.OVERVIEW ? (
            <Box
              role="tabpanel"
              hidden={ViewTypes.OVERVIEW !== selectedComponentStation}
              sx={{ width: '100%', flex: 1, height: 'calc(100% - 200px)' }}
            >
              <SectionTable
                tableName={view}
                shortages={shortages}
                foodComponents={overviewComponents}
                key={view}
                showAllUnits={showAllUnits}
                selectedHours={selectedHours}
                selectedIdsSet={selectedIdsSet}
                onComponentSelection={setSelectedComponents}
                isTablet={isTablet}
              />
            </Box>
          ) : (
            selectedComponentStation in FoodComponentStationTitle &&
            selectedComponentStation !== 'portioning' && (
              <Box role="tabpanel" sx={{ width: '100%', flex: 1, height: 'calc(100% - 200px)' }}>
                <SectionTable
                  key={selectedComponentStation}
                  tableName={selectedComponentStation}
                  shortages={shortages}
                  foodComponents={stationComponents}
                  highlightedComponentId={highlightedComponentId}
                  onHighlightEnd={clearHighlight}
                  showAllUnits={showAllUnits}
                  selectedHours={selectedHours}
                  selectedIdsSet={selectedIdsSet}
                  onComponentSelection={setSelectedComponents}
                  isTablet={isTablet}
                />
              </Box>
            )
          )
        ) : (
          <Box
            sx={{
              width: '100%',
              flex: 1
            }}
          >
            {!componentsLoading && <EmptyMessage label="No Data" />}
          </Box>
        )}
        <DownloadPopup exportComponentStatusModalRef={exportComponentStatusModalRef} theme={theme} kitchen={kitchen} />
        <ComponentWeightBufferEditor componentWeightBufferEditorModalRef={componentWeightBufferEditorModalRef} theme={theme} />
        <FiltersDrawer isFilterOpened={isFilterOpened} setIsFilterOpened={() => setIsFilterOpened((prev) => !prev)}>
          <FilterItems
            filters={componentCheckFilters}
            setFilters={setComponentCheckFilters}
            getDefaultFilters={getDefaultFilters}
            toggleFilersDrawer={(bool: boolean) => setIsFilterOpened(bool)}
          ></FilterItems>
        </FiltersDrawer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mx: '30px' }}></Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={componentsLoading && !appContext.isOffline && !componentsHasNext}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
    );
  }
};

export default ComponentsCheck;
